import { useFetch } from "~vue/api";
import type { SortableMove } from "~vue/composables/useSortableList";
import type { BuilderChoices, BuilderDocument, BuilderPayload, BuilderQuestion, BuilderSection, BuilderTemplate } from "~vue/SurveyBuilder/types";
import { computed, type Ref, ref } from "vue";

/** `data-list-id` used by the outline for the bucket of section-less questions. */
export const UNSECTIONED = "unsectioned";
/** `data-list-id` of the top-level section list. */
export const ROOT = "root";

const newRef = () => `new-${globalThis.crypto.randomUUID()}`;

function withClientIds(loaded: BuilderDocument): BuilderDocument {
    return {
        ...loaded,
        questions: loaded.questions.map((question) => ({ ...question, client_id: question.client_id || question.id || newRef() })),
    };
}

/**
 * Re-label saved sections with the refs the editor already uses for them,
 * matching on the section code (unique within a survey).
 *
 * A section the browser invented has a `new-…` ref, and the server answers with
 * the row's id instead; adopting that verbatim changes every `v-for` key, so Vue
 * tears down and rebuilds the whole outline on save — losing focus and drag
 * state for an edit that changed nothing the author can see.
 */
function withEditorRefs(loaded: BuilderDocument, previous: BuilderDocument | null): BuilderDocument {
    if (!previous) {
        return loaded;
    }
    const editorRefByCode = new Map(previous.sections.map((section) => [section.code, section.ref]));
    const editorRef = (savedRef: string) => {
        const section = loaded.sections.find((candidate) => candidate.ref === savedRef);
        return (section && editorRefByCode.get(section.code)) || savedRef;
    };

    return {
        ...loaded,
        sections: loaded.sections.map((section) => ({
            ...section,
            ref: editorRefByCode.get(section.code) ?? section.ref,
            parent_ref: section.parent_ref === null ? null : editorRef(section.parent_ref),
        })),
        questions: loaded.questions.map((question) => ({
            ...question,
            section_ref: question.section_ref === null ? null : editorRef(question.section_ref),
        })),
    };
}

export interface SurveyBuilder {
    document: Ref<BuilderDocument | null>;
    choices: Ref<BuilderChoices | null>;
    loading: Ref<boolean>;
    loadError: Ref<string | null>;
    saving: Ref<boolean>;
    saveError: Ref<string | null>;
    dirty: Ref<boolean>;
    /** Canonical builder URL for the saved survey, once it exists. */
    builderUrl: Ref<string>;
    /** Assessments already run on this survey — edits reach their future raters. */
    cycleCount: Ref<number>;
    /** Django admin page for this survey (its changelist until the first save). */
    adminUrl: Ref<string>;
    hasResponses: (questionId: string | null) => boolean;
    topSections: Ref<BuilderSection[]>;
    childSectionsOf: (ref: string) => BuilderSection[];
    questionsOf: (sectionRef: string | null) => BuilderQuestion[];
    orderedDocument: Ref<BuilderDocument | null>;
    updateTemplate: (changes: Partial<BuilderTemplate>) => void;
    updateSection: (sectionRef: string, changes: Partial<BuilderSection>) => void;
    updateQuestion: (clientId: string, changes: Partial<BuilderQuestion>) => void;
    addSection: (parentRef?: string | null) => void;
    removeSection: (sectionRef: string) => void;
    addQuestion: (sectionRef: string | null) => BuilderQuestion | null;
    duplicateQuestion: (clientId: string) => void;
    removeQuestion: (clientId: string) => void;
    applyQuestionMove: (move: SortableMove) => void;
    applySectionMove: (move: SortableMove) => void;
    load: () => Promise<void>;
    save: () => Promise<boolean>;
}

/**
 * Editing state for one survey: the document plus the operations the builder
 * UI performs on it, and the load/save round-trips to the Django endpoints.
 *
 * The page is served with `loadUrl` and `saveUrl` reversed by Django; for a
 * survey that doesn't exist yet they point at the blank-payload and create
 * endpoints, and the response carries the real ones from then on.
 */
export function useSurveyBuilder(loadUrl: string, initialSaveUrl: string): SurveyBuilder {
    const surveyDocument = ref<BuilderDocument | null>(null);
    const choices = ref<BuilderChoices | null>(null);
    const loading = ref(true);
    const loadError = ref<string | null>(null);
    const saving = ref(false);
    const saveError = ref<string | null>(null);
    const saveUrl = ref(initialSaveUrl);
    const builderUrl = ref("");
    const answeredQuestionIds = ref<Set<string>>(new Set());
    const savedSnapshot = ref<string>("");
    const cycleCount = ref(0);
    const adminUrl = ref("");

    const dirty = computed(() => (surveyDocument.value ? JSON.stringify(surveyDocument.value) !== savedSnapshot.value : false));

    const topSections = computed(() => (surveyDocument.value?.sections ?? []).filter((section) => !section.parent_ref));

    const childSectionsOf = (sectionRef: string) => (surveyDocument.value?.sections ?? []).filter((section) => section.parent_ref === sectionRef);

    const questionsOf = (sectionRef: string | null) => (surveyDocument.value?.questions ?? []).filter((question) => question.section_ref === sectionRef);

    /**
     * The document in the order a participant meets it: sections depth-first,
     * each section's questions in place, section-less questions last. This is
     * the order the API serves questions in, so both preview and save use it.
     */
    const orderedDocument = computed<BuilderDocument | null>(() => {
        if (!surveyDocument.value) {
            return null;
        }
        const sections: BuilderSection[] = [];
        const questions: BuilderQuestion[] = [];
        const visit = (section: BuilderSection, order: number) => {
            sections.push({ ...section, order });
            questionsOf(section.ref).forEach((question, index) => questions.push({ ...question, order: index }));
            childSectionsOf(section.ref).forEach(visit);
        };
        topSections.value.forEach(visit);
        questionsOf(null).forEach((question, index) => questions.push({ ...question, order: index }));
        return { template: surveyDocument.value.template, sections, questions };
    });

    const hasResponses = (questionId: string | null) => (questionId ? answeredQuestionIds.value.has(questionId) : false);

    function adopt(payload: BuilderPayload) {
        surveyDocument.value = withClientIds(withEditorRefs(payload.document, surveyDocument.value));
        choices.value = payload.choices;
        answeredQuestionIds.value = new Set(payload.question_states.filter((state) => state.has_responses).map((state) => state.id));
        saveUrl.value = payload.save_url;
        builderUrl.value = payload.builder_url;
        cycleCount.value = payload.cycle_count;
        adminUrl.value = payload.admin_url;
        savedSnapshot.value = JSON.stringify(surveyDocument.value);
    }

    function updateTemplate(changes: Partial<BuilderTemplate>) {
        if (surveyDocument.value) {
            Object.assign(surveyDocument.value.template, changes);
        }
    }

    function updateSection(sectionRef: string, changes: Partial<BuilderSection>) {
        const section = surveyDocument.value?.sections.find((candidate) => candidate.ref === sectionRef);
        if (section) {
            Object.assign(section, changes);
        }
    }

    function updateQuestion(clientId: string, changes: Partial<BuilderQuestion>) {
        const question = surveyDocument.value?.questions.find((candidate) => candidate.client_id === clientId);
        if (question) {
            Object.assign(question, changes);
        }
    }

    function addSection(parentRef: string | null = null) {
        if (!surveyDocument.value) {
            return;
        }
        const siblings = parentRef ? childSectionsOf(parentRef) : topSections.value;
        surveyDocument.value.sections.push({
            id: null,
            ref: newRef(),
            parent_ref: parentRef,
            order: siblings.length,
            code: "",
            title: "",
            description: "",
            instructions: "",
            enabled: true,
        });
    }

    function removeSection(sectionRef: string) {
        if (!surveyDocument.value) {
            return;
        }
        const removed = new Set([sectionRef, ...childSectionsOf(sectionRef).map((section) => section.ref)]);
        surveyDocument.value.sections = surveyDocument.value.sections.filter((section) => !removed.has(section.ref));
        surveyDocument.value.questions = surveyDocument.value.questions.filter((question) => !question.section_ref || !removed.has(question.section_ref));
    }

    function addQuestion(sectionRef: string | null) {
        if (!surveyDocument.value) {
            return null;
        }
        const question: BuilderQuestion = {
            id: null,
            client_id: newRef(),
            section_ref: sectionRef,
            order: questionsOf(sectionRef).length,
            question_type: "Score",
            title: "",
            text: "",
            scale: null,
            is_optional: false,
            enabled: true,
        };
        surveyDocument.value.questions.push(question);
        return question;
    }

    function duplicateQuestion(clientId: string) {
        if (!surveyDocument.value) {
            return;
        }
        const source = surveyDocument.value.questions.find((question) => question.client_id === clientId);
        if (!source) {
            return;
        }
        const copy: BuilderQuestion = { ...source, id: null, client_id: newRef() };
        const index = surveyDocument.value.questions.indexOf(source);
        surveyDocument.value.questions.splice(index + 1, 0, copy);
    }

    function removeQuestion(clientId: string) {
        if (!surveyDocument.value) {
            return;
        }
        surveyDocument.value.questions = surveyDocument.value.questions.filter((question) => question.client_id !== clientId);
    }

    function applyQuestionMove(move: SortableMove) {
        if (!surveyDocument.value) {
            return;
        }
        const fromSection = move.fromListId === UNSECTIONED ? null : move.fromListId;
        const toSection = move.toListId === UNSECTIONED ? null : move.toListId;
        const moved = questionsOf(fromSection)[move.oldIndex];
        if (!moved) {
            return;
        }
        const rest = surveyDocument.value.questions.filter((question) => question !== moved);
        const target = rest.filter((question) => question.section_ref === toSection);
        const anchor = target[move.newIndex];
        moved.section_ref = toSection;
        const insertAt = anchor ? rest.indexOf(anchor) : rest.length;
        rest.splice(insertAt, 0, moved);
        surveyDocument.value.questions = rest;
    }

    function applySectionMove(move: SortableMove) {
        if (!surveyDocument.value) {
            return;
        }
        const fromParent = move.fromListId === ROOT ? null : move.fromListId;
        const toParent = move.toListId === ROOT ? null : move.toListId;
        const moved = (fromParent ? childSectionsOf(fromParent) : topSections.value)[move.oldIndex];
        if (!moved) {
            return;
        }
        const rest = surveyDocument.value.sections.filter((section) => section !== moved);
        moved.parent_ref = toParent;
        const anchor = rest.filter((section) => section.parent_ref === toParent)[move.newIndex];
        rest.splice(anchor ? rest.indexOf(anchor) : rest.length, 0, moved);
        surveyDocument.value.sections = rest;
    }

    async function load() {
        loading.value = true;
        try {
            const { data, error } = await useFetch(loadUrl).get().json();
            if (error.value) {
                loadError.value = (data.value as { error?: string })?.error ?? "Could not load this survey.";
                return;
            }
            adopt(data.value as BuilderPayload);
        } finally {
            loading.value = false;
        }
    }

    async function save() {
        if (!surveyDocument.value || !orderedDocument.value) {
            return false;
        }
        saving.value = true;
        saveError.value = null;
        // `finally`, because a request that rejects rather than resolving with an
        // error (a cancelled fetch, a CSRF refresh that throws) would otherwise
        // leave the button stuck on "Saving…" until the page is reloaded.
        try {
            const { data, error } = await useFetch(saveUrl.value).post(orderedDocument.value).json();
            if (error.value) {
                saveError.value = (data.value as { error?: string })?.error ?? "Could not save this survey.";
                return false;
            }
            adopt((data.value as { payload: BuilderPayload }).payload);
            return true;
        } finally {
            saving.value = false;
        }
    }

    return {
        document: surveyDocument,
        choices,
        loading,
        loadError,
        saving,
        saveError,
        dirty,
        builderUrl,
        cycleCount,
        adminUrl,
        hasResponses,
        topSections,
        childSectionsOf,
        questionsOf,
        orderedDocument,
        updateTemplate,
        updateSection,
        updateQuestion,
        addSection,
        removeSection,
        addQuestion,
        duplicateQuestion,
        removeQuestion,
        applyQuestionMove,
        applySectionMove,
        load,
        save,
    };
}
